// === Sentry + PostHog: сбор ТОЛЬКО после согласия (переписано 27.08.2026) ===
//
// Было: и сбор ошибок с записью экрана, и аналитика поднимались при загрузке страницы —
// до того, как человек что-либо разрешил. При этом наша политика конфиденциальности
// уже обещала возможность отказаться, а в продукте её не было. Две разные претензии
// сразу: сбор без основания и невыполненное обещание.
//
// Стало: пока человек не нажал «Разрешить» в баннере, не инициализируется НИЧЕГО.
// Ответ живёт в localStorage и меняется в любой момент — там же, в баннере, и в
// подвале страницы. Отказ действует немедленно, а не со следующего визита.

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

const ENV = process.env.NODE_ENV || 'development';
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

const KEY = 'diagConsent';          // '1' разрешил · '0' отказал · нет ключа — не спрашивали

// localStorage может быть недоступен (приватное окно, запрет на данные сайтов) —
// тогда считаем, что согласия нет. Молча ничего не собираем.
const readStored = () => {
  try { return localStorage.getItem(KEY); } catch (_) { return null; }
};

let consent = readStored() === '1';
let started = false;

export const consentAsked = () => readStored() !== null;
export const hasConsent = () => consent;

const start = () => {
  if (started) return;
  started = true;

  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENV,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: ENV === 'production' ? 0.1 : 0,
      replaysSessionSampleRate: 0,                      // запись экрана — только при ошибке
      replaysOnErrorSampleRate: 1.0,
    });
  }

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
    });
  }
};

// Сохранить решение. Разрешил — поднимаем сбор сразу; отказался — гасим немедленно
// и забываем, кто это был.
export const setConsent = (granted) => {
  consent = !!granted;
  try { localStorage.setItem(KEY, granted ? '1' : '0'); } catch (_) { /* не смогли — ну и ладно */ }

  if (granted) {
    start();
    return;
  }

  try {
    if (POSTHOG_KEY && started) { posthog.reset(); posthog.opt_out_capturing(); }
    if (SENTRY_DSN && started) {
      // Забыть, кто это был, мало: клиент Sentry продолжал бы слать ошибки и запись
      // экрана при сбое. Отзыв согласия обязан ГАСИТЬ сбор, а не обезличивать его.
      Sentry.getCurrentScope().setUser(null);
      const client = Sentry.getClient();
      if (client) client.close();
      started = false;                 // повторное согласие поднимет SDK заново
    }
  } catch (_) { /* гасим тихо */ }
};

if (consent) start();

// Обёртки — безопасны, когда сбор выключен или согласия нет
export const track = (event, props = {}) => {
  if (consent && POSTHOG_KEY && started) posthog.capture(event, props);
};

export const identify = (userId, traits = {}) => {
  if (!consent) return;
  if (POSTHOG_KEY && started) posthog.identify(userId, traits);
  if (SENTRY_DSN && started) Sentry.setUser({ id: userId, ...traits });
};

export const reset = () => {
  if (!consent) return;
  if (POSTHOG_KEY && started) posthog.reset();
  if (SENTRY_DSN && started) Sentry.setUser(null);
};

export { Sentry, posthog };
